import type { PrinterAdapter, PrinterDevice, PrinterDriver } from '@trackeat/shared';
import { ZplAdapter } from './zpl.js';
import { EscPosAdapter } from './escpos.js';
import { BrotherQlAdapter } from './brother-ql.js';
import { TsplAdapter } from './tspl.js';
import { CpclAdapter } from './cpcl.js';
import { NiimbotAdapter } from './niimbot.js';
import { PreviewAdapter } from './preview.js';
import { SystemPrintAdapter } from './system-print.js';
import { QzPrintAdapter } from './qz-print.js';

export { renderTemplate, renderElements, resolveBinding } from './renderer.js';
export type { RenderedElement } from './renderer.js';
export { buildZpl, ZplAdapter } from './zpl.js';
export { buildEscPos, EscPosAdapter } from './escpos.js';
export { BrotherQlAdapter } from './brother-ql.js';
export { buildTspl, TsplAdapter } from './tspl.js';
export { buildCpcl, CpclAdapter } from './cpcl.js';
export { NiimbotAdapter } from './niimbot.js';
export { PreviewAdapter, PREVIEW_PRINTER_ID, PREVIEW_PRINTER_NAME } from './preview.js';
export {
  SystemPrintAdapter,
  SYSTEM_PRINTER_ID,
  SYSTEM_PRINTER_NAME,
  SYSTEM_PRINTER_ADDRESS,
} from './system-print.js';
export { QzPrintAdapter } from './qz-print.js';
export { qzConnect, qzListPrinters, qzIsAvailable } from './qz-transport.js';
export { bleScanPrinters, bleConnect, bleDisconnect, bleWriteBytes } from './ble-transport.js';
export type { BlePrinterDevice } from './ble-transport.js';

export const createPrinterAdapter = (driver: PrinterDriver): PrinterAdapter => {
  switch (driver) {
    case 'zpl':
      return new ZplAdapter();
    case 'escpos':
      return new EscPosAdapter();
    case 'brother_ql':
      return new BrotherQlAdapter();
    case 'tspl':
      return new TsplAdapter();
    case 'cpcl':
      return new CpclAdapter();
    case 'niimbot':
      return new NiimbotAdapter();
    case 'preview':
      return new PreviewAdapter();
    case 'system':
      return new SystemPrintAdapter();
    case 'qz':
      return new QzPrintAdapter();
    default: {
      const exhaustive: never = driver;
      throw new Error(`Driver stampante non supportato: ${String(exhaustive)}`);
    }
  }
};

const ALL_DRIVERS: PrinterDriver[] = ['brother_ql', 'zpl', 'escpos', 'tspl', 'cpcl'];

/**
 * Scan BLE + mDNS (Wi-Fi) across every adapter and return a deduplicated list
 * of discovered printers. Each adapter is queried in parallel; failures in one
 * adapter do not block the others.
 */
export const autoDiscover = async (): Promise<PrinterDevice[]> => {
  const adapters = ALL_DRIVERS.map(createPrinterAdapter);
  const results = await Promise.allSettled(adapters.map((a) => a.discover()));
  const seen = new Set<string>();
  const merged: PrinterDevice[] = [];
  for (const res of results) {
    if (res.status !== 'fulfilled') continue;
    for (const dev of res.value) {
      const key = `${dev.driver}:${dev.connection}:${dev.address}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(dev);
    }
  }
  return merged;
};
